import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Share2, ArrowUp, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_OPTIONS = [
  'YouTube', 'Instagram', 'Facebook', 'TikTok', 'X / Twitter',
  'Website', 'Newsletter', 'Podcast', 'LinkedIn', 'Other'
];

export const SocialLinksTab = ({ onMessage }) => {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({ platform: 'YouTube', url: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API}/social-links`);
      setLinks(data);
    } catch (e) { console.error(e); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/social-links`, {
        platform: form.platform, url: form.url, order: links.length
      }, { withCredentials: true });
      setForm({ platform: 'YouTube', url: '' });
      onMessage('Link added');
      fetchAll();
    } catch (err) {
      onMessage(err.response?.data?.detail || 'Failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    await axios.delete(`${API}/admin/social-links/${id}`, { withCredentials: true });
    onMessage('Link deleted');
    fetchAll();
  };

  const reorder = async (idx, dir) => {
    const newLinks = [...links];
    const target = idx + dir;
    if (target < 0 || target >= newLinks.length) return;
    [newLinks[idx], newLinks[target]] = [newLinks[target], newLinks[idx]];
    // Persist new orders
    await Promise.all(newLinks.map((l, i) =>
      axios.put(`${API}/admin/social-links/${l.id}`, { order: i }, { withCredentials: true })
    ));
    fetchAll();
  };

  return (
    <div data-testid="admin-social-links-tab" className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
          <Share2 className="w-6 h-6 text-blue-600" /> Connect With Us
        </h2>
        <p className="text-sm text-slate-600 mb-6">
          Add your YouTube, Instagram, podcast, newsletter, and other public links. These appear on the public mission page.
        </p>

        <form data-testid="add-social-form" onSubmit={add} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <select data-testid="social-platform-select" value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  className="px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500">
            {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input data-testid="social-url-input" type="url" required
                 placeholder="https://..." value={form.url}
                 onChange={(e) => setForm({ ...form, url: e.target.value })}
                 className="md:col-span-1 px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
          <button data-testid="add-social-btn" type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> Add
          </button>
        </form>

        {links.length === 0 ? (
          <p className="text-slate-500 text-sm">No social links yet.</p>
        ) : (
          <div className="space-y-2">
            {links.map((l, i) => (
              <div key={l.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium flex-shrink-0">{l.platform}</span>
                <p className="flex-1 text-sm text-slate-600 truncate">{l.url}</p>
                <button onClick={() => reorder(i, -1)} disabled={i === 0}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => reorder(i, 1)} disabled={i === links.length - 1}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button data-testid={`delete-social-btn-${l.id}`} onClick={() => del(l.id)}
                        className="text-red-600 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
